import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { Document } from '@/types/document/document'

interface DocumentFolderType<TValue> {
  [id: string]: TValue;
}

export const documentStore = defineStore('document', () => {
  const loading = ref(false)
  const activeTab = ref<string|null>(null)
  const documents = ref<DocumentFolderType<{ name:string, documents:Document[] }>|null>(null)
  const originalDocuments = ref<DocumentFolderType<{ name: string, documents: Document[] }> | null>(null)

  const sortOrder = ref<'asc' | 'desc'>('asc')
  const sortBy = ref<'default' | 'name' | 'date'>('default')

  function getDocuments () : void {
    loading.value = true
    axios.get(process.env.VUE_APP_API_URL + '/building/documents').then((r) => {
      documents.value = r.data
      originalDocuments.value = JSON.parse(JSON.stringify(r.data))

      if (documents.value && !Array.isArray(documents.value)) {
        activeTab.value = documents.value[Object.entries(documents.value)[0][0]].name || null
      }
    }).finally(() => {
      loading.value = false
    })
  }

  function toggleSortOrder () : void {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    sortDocuments()
  }

  function setSortBy (value: 'default' | 'name' | 'date') : void {
    if (sortBy.value === value) return
    sortBy.value = value
    sortDocuments()
  }

  function compareDates (a: Document, b: Document) : number {
    const dateA = new Date(a.date.split('-').reverse().join('-'))
    const dateB = new Date(b.date.split('-').reverse().join('-'))
    return dateA.getTime() - dateB.getTime()
  }

  function sortDocuments () : void {
    if (!documents.value) return

    documents.value = Object.entries(documents.value).reduce((acc, [key, folder]) => {
      const sortedDocs = [...folder.documents]

      if (sortBy.value === 'name') {
        sortedDocs.sort((a, b) => a.name.localeCompare(b.name))
      }
      if (sortBy.value === 'date') {
        sortedDocs.sort(compareDates)
      }

      if (sortOrder.value === 'desc') {
        sortedDocs.reverse()
      }

      acc[key] = { name: folder.name, documents: sortedDocs }
      return acc
    }, {} as DocumentFolderType<{ name: string, documents: Document[] }>)
  }

  return { loading, activeTab, documents, getDocuments, setSortBy, toggleSortOrder, originalDocuments, sortOrder }
})
